import { theme as antdTheme } from 'antd';

import type { ThemeConfig } from 'antd';

/**
 * Дизайн-токены админки.
 *
 * Палитра подобрана под fintech-эстетику: строгий тёмно-синий primary,
 * холодные нейтральные поверхности, янтарный акцент для важных действий.
 * Без теней на основных поверхностях — только тонкие границы.
 */
const tokens = {
  navy900: '#0F172A',
  navy800: '#1E293B',
  navy700: '#334155',
  slate500: '#64748B',
  slate400: '#94A3B8',
  slate300: '#CBD5E1',
  slate200: '#E2E8F0',
  slate100: '#F1F5F9',
  slate50: '#F8FAFC',
  white: '#FFFFFF',
  amber600: '#D97706',
  emerald600: '#059669',
  red600: '#DC2626',
};

/**
 * AntD ThemeConfig для светлой темы.
 *
 * Стилистика: Linear/Stripe-подобный fintech-look — высокая плотность,
 * чёткая типографика, минимум теней, ровные радиусы.
 */
export const lightTheme: ThemeConfig = {
  algorithm: antdTheme.defaultAlgorithm,
  cssVar: true,
  token: {
    colorPrimary: tokens.navy900,
    colorInfo: tokens.navy900,
    colorSuccess: tokens.emerald600,
    colorWarning: tokens.amber600,
    colorError: tokens.red600,

    colorBgLayout: tokens.slate50,
    colorBgContainer: tokens.white,
    colorBgElevated: tokens.white,
    colorBgSpotlight: tokens.navy900,

    colorBorder: tokens.slate200,
    colorBorderSecondary: tokens.slate100,

    colorText: tokens.navy900,
    colorTextSecondary: tokens.navy700,
    colorTextTertiary: tokens.slate500,
    colorTextQuaternary: tokens.slate400,

    borderRadius: 6,
    borderRadiusLG: 8,
    borderRadiusSM: 4,

    fontFamily:
      '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    fontFamilyCode:
      '"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
    fontSize: 14,
    fontSizeHeading1: 28,
    fontSizeHeading2: 22,
    fontSizeHeading3: 18,
    fontSizeHeading4: 16,
    fontSizeSM: 13,
    lineHeight: 1.5,

    controlHeight: 36,
    controlHeightLG: 44,
    controlHeightSM: 28,

    sizeUnit: 4,
    sizeStep: 4,

    boxShadow: '0 1px 2px 0 rgba(15, 23, 42, 0.04)',
    boxShadowSecondary: '0 4px 12px -2px rgba(15, 23, 42, 0.08)',
    boxShadowTertiary: 'none',

    wireframe: false,
    motion: true,
    motionDurationFast: '0.1s',
    motionDurationMid: '0.15s',
    motionDurationSlow: '0.2s',
  },
  components: {
    Layout: {
      headerBg: tokens.white,
      siderBg: tokens.white,
      bodyBg: tokens.slate50,
      headerHeight: 56,
      headerPadding: '0 24px',
      triggerBg: tokens.slate100,
      triggerColor: tokens.navy700,
    },
    Menu: {
      itemBg: 'transparent',
      itemSelectedBg: tokens.slate100,
      itemSelectedColor: tokens.navy900,
      itemHoverBg: tokens.slate50,
      itemHoverColor: tokens.navy900,
      itemColor: tokens.navy700,
      itemActiveBg: tokens.slate100,
      itemHeight: 36,
      itemMarginInline: 8,
      itemPaddingInline: 12,
      iconSize: 16,
      collapsedIconSize: 18,
      groupTitleColor: tokens.slate500,
      subMenuItemBg: 'transparent',
    },
    Button: {
      controlHeight: 36,
      borderRadius: 6,
      fontWeight: 500,
      primaryShadow: 'none',
      defaultShadow: 'none',
      dangerShadow: 'none',
    },
    Input: {
      controlHeight: 36,
      borderRadius: 6,
      activeShadow: '0 0 0 3px rgba(15, 23, 42, 0.08)',
      hoverBorderColor: tokens.navy700,
      activeBorderColor: tokens.navy900,
    },
    Select: {
      controlHeight: 36,
      borderRadius: 6,
    },
    Table: {
      cellPaddingBlock: 12,
      cellPaddingInline: 16,
      headerBg: tokens.slate50,
      headerColor: tokens.slate500,
      headerSplitColor: tokens.slate200,
      borderColor: tokens.slate200,
      rowHoverBg: tokens.slate50,
      headerSortHoverBg: tokens.slate100,
      headerSortActiveBg: tokens.slate100,
      fontSize: 14,
      fontWeightStrong: 500,
    },
    Card: {
      borderRadiusLG: 8,
      boxShadowTertiary: 'none',
      colorBorderSecondary: tokens.slate200,
      paddingLG: 24,
    },
    Form: {
      labelColor: tokens.navy700,
      labelFontSize: 13,
      itemMarginBottom: 20,
      verticalLabelPadding: '0 0 6px',
    },
    Modal: {
      borderRadiusLG: 10,
      titleFontSize: 18,
      titleLineHeight: 1.4,
    },
    Tag: {
      borderRadiusSM: 4,
      defaultBg: tokens.slate100,
      defaultColor: tokens.navy700,
    },
    Tabs: {
      itemActiveColor: tokens.navy900,
      itemSelectedColor: tokens.navy900,
      itemHoverColor: tokens.navy900,
      inkBarColor: tokens.navy900,
      titleFontSize: 14,
    },
    Notification: {
      borderRadiusLG: 8,
    },
    Tooltip: {
      colorBgSpotlight: tokens.navy900,
      colorTextLightSolid: tokens.white,
      borderRadius: 6,
    },
    Divider: {
      colorSplit: tokens.slate200,
    },
    Typography: {
      titleMarginBottom: '0.4em',
      titleMarginTop: '1.2em',
    },
  },
};

export const designTokens = tokens;
